import React, { useState } from 'react';

const LocaisManagement = ({ locais, onCreateLocal, onUpdateLocal, onDeleteLocal, setIsEditing }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLocal, setSelectedLocal] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLocais = locais.filter(local =>
    !searchTerm.trim() || local.nome.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const isValidCoordinates = (lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    return !isNaN(latitude) && latitude >= 36.0 && latitude <= 42.0 &&
           !isNaN(longitude) && longitude >= -10.0 && longitude <= -6.0;
  };

  const isFormValid = () => {
    return formData.nome?.trim() && isValidCoordinates(formData.latitude, formData.longitude);
  };

  const handleCreateLocal = () => {
    setFormData({
      nome: '',
      latitude: '',
      longitude: '',
      frequencia: 1
    });
    setShowCreateModal(true);
    setIsEditing(true);
  };

  const handleEditLocal = (local) => {
    setSelectedLocal(local);
    setFormData({
      nome: local.nome,
      latitude: local.latitude,
      longitude: local.longitude,
      frequencia: local.frequencia
    });
    setShowEditModal(true);
    setIsEditing(true);
  };

  const handleDeleteLocal = async (local) => {
    const confirmed = window.confirm(
      `Tem a certeza que deseja eliminar o local "${local.nome}"?\n\n` +
      `Este local deixará de ser sugerido à aplicação móvel. ` +
      `Poderá ser recriado automaticamente se os voluntários continuarem a registar paragens nestas coordenadas.`
    );

    if (confirmed) {
      try {
        await onDeleteLocal(local.id);
      } catch (error) {
        alert('Erro ao eliminar local: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const submitCreateLocal = async () => {
    if (!formData.nome?.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    if (!isValidCoordinates(formData.latitude, formData.longitude)) {
      alert('Coordenadas inválidas. Latitude: 36 a 42, Longitude: -10 a -6 (Portugal)');
      return;
    }

    try {
      await onCreateLocal({
        nome: formData.nome.trim(),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        frequencia: parseInt(formData.frequencia) || 1
      });
      setShowCreateModal(false);
      setFormData({});
      setIsEditing(false);
    } catch (error) {
      alert('Erro ao criar local: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitEditLocal = async () => {
    if (!formData.nome?.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    if (!isValidCoordinates(formData.latitude, formData.longitude)) {
      alert('Coordenadas inválidas. Latitude: 36 a 42, Longitude: -10 a -6 (Portugal)');
      return;
    }

    try {
      await onUpdateLocal(selectedLocal.id, {
        nome: formData.nome.trim(),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        frequencia: parseInt(formData.frequencia) || 0
      });
      setShowEditModal(false);
      setSelectedLocal(null);
      setFormData({});
      setIsEditing(false);
    } catch (error) {
      alert('Erro ao atualizar local: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedLocal(null);
    setFormData({});
    setIsEditing(false);
  };

  const renderFormFields = () => (
    <>
      <div className="form-group">
        <label htmlFor="local_nome">Nome do Local *</label>
        <input
          type="text"
          id="local_nome"
          className="form-control"
          value={formData.nome || ''}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          placeholder="Ex: Praça do Comércio"
          maxLength={200}
          required
        />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="local_latitude">Latitude * (36 a 42)</label>
          <input
            type="number"
            id="local_latitude"
            className="form-control"
            step="0.000001"
            value={formData.latitude ?? ''}
            onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
            placeholder="Ex: 38.708"
            required
          />
        </div>

        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="local_longitude">Longitude * (-10 a -6)</label>
          <input
            type="number"
            id="local_longitude"
            className="form-control"
            step="0.000001"
            value={formData.longitude ?? ''}
            onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
            placeholder="Ex: -9.137"
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="local_frequencia">Frequência</label>
        <input
          type="number"
          id="local_frequencia"
          className="form-control"
          min={0}
          value={formData.frequencia ?? ''}
          onChange={(e) => setFormData({ ...formData, frequencia: e.target.value })}
        />
        <small className="form-text">
          Número de visitas registadas. Locais com maior frequência têm prioridade nas sugestões por nome.
        </small>
      </div>
    </>
  );

  return (
    <>
      <div className="card-header">
        <h3>Gestão de Locais Visitados</h3>
        <button className="btn btn-success" onClick={handleCreateLocal}>
          + Novo Local
        </button>
      </div>

      <div className="form-group" style={{ padding: '0 1rem' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Pesquisar por nome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {locais.length === 0 ? (
        <div className="empty-state">
          <h3>Nenhum local encontrado</h3>
          <p>Os locais são criados automaticamente a partir das paragens registadas pela aplicação móvel, ou pode criar um manualmente.</p>
          <button className="btn btn-primary" onClick={handleCreateLocal}>
            Criar Local
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Frequência</th>
                <th>Última Visita</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocais.map(local => (
                <tr key={local.id}>
                  <td><strong>{local.nome}</strong></td>
                  <td>{Number(local.latitude).toFixed(6)}</td>
                  <td>{Number(local.longitude).toFixed(6)}</td>
                  <td>
                    <span className={`badge ${local.frequencia > 0 ? 'primary' : 'secondary'}`}>
                      {local.frequencia}
                    </span>
                  </td>
                  <td>{formatDate(local.ultima_visita)}</td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleEditLocal(local)}
                    >
                      Editar
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteLocal(local)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredLocais.length === 0 && (
            <div className="empty-state">
              <p>Nenhum local corresponde à pesquisa "{searchTerm}".</p>
            </div>
          )}
        </div>
      )}

      {/* Create Local Modal */}
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Criar Novo Local</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>

            {renderFormFields()}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Cancelar
              </button>
              <button
                className="btn btn-success"
                onClick={submitCreateLocal}
                disabled={!isFormValid()}
              >
                Criar Local
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Local Modal */}
      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Editar Local</h3>
              <button className="close-btn" onClick={closeModals}>×</button>
            </div>

            {renderFormFields()}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModals}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={submitEditLocal}
                disabled={!isFormValid()}
              >
                Guardar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocaisManagement;
